import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { api, imgUrl } from "@/lib/api";
import { ArrowRight, ArrowUpRight, Loader2, Star, Check, Search, ChevronLeft, ChevronRight, BadgeCheck, MoreHorizontal, Heart, Info } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import SignupForm from "@/components/SignupForm";

const BLUE = "#1e3cff";
const LIME = "#ccff00";
const PINK = "#ff2d8e";
const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/0a46be76-d1d2-4156-a6df-b2b75b9d7a3d/images/6b6c46bdd1f47827f8addbda67adca55232a77165aa8d1d9c312fb0fe770d925.jpeg";
const anton = { fontFamily: "Anton, Archivo, sans-serif" };
import { openCookieSettings } from "@/lib/consent";
const LEGAL_LINKS = [
  { slug: "terms", label: "Terms of Service" },
  { slug: "privacy", label: "Privacy Policy" },
  { slug: "cookies", label: "Cookie Policy" },
  { slug: "refunds", label: "Returns & Refunds" },
  { slug: "disclaimer", label: "Disclaimer" },
  { slug: "imprint", label: "Contact & Imprint" },
];
const DEFAULT_DISCLAIMER = "StoreIG is an independent platform and is not affiliated with, endorsed by, sponsored by, or in any way officially connected to Instagram or Meta Platforms, Inc.";
const CAT_ACCENTS = [PINK, LIME, "#ff7a00", "#8b5cf6", "#00c2ff", "#00d478", "#ffcc00", "#ff4d4d"];

const ChromeStar = ({ className, style }) => (
  <svg viewBox="0 0 100 100" className={className} style={style} aria-hidden>
    <defs>
      <linearGradient id="chrome" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#ffffff" />
        <stop offset="45%" stopColor="#c9d2dc" />
        <stop offset="100%" stopColor="#7c8794" />
      </linearGradient>
    </defs>
    <path d="M50 2 L61 39 L98 50 L61 61 L50 98 L39 61 L2 50 L39 39 Z" fill="url(#chrome)" stroke="#5b6570" strokeWidth="2" />
  </svg>
);

export default function DirectoryV3({ content, stores, loading }) {
  const [signupOpen, setSignupOpen] = useState(false);
  const [subscribing, setSubscribing] = useState(null);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  const handlePlan = async (p, i) => {
    const amount = parseFloat(String(p.price).replace(/[^\d.,]/g, "").replace(",", "."));
    if (content.subscriptions_enabled && amount > 0) {
      setSubscribing(i);
      try {
        const res = await api.post("/payments/subscribe", { plan_index: i, plan_id: p.id || "", origin_url: window.location.origin });
        window.location.href = res.data.checkout_url;
      } catch (e) { toast.error(e.response?.data?.detail || "Could not start checkout"); setSubscribing(null); }
    } else { setSignupOpen(true); }
  };

  const categories = Array.from(new Set(stores.map((s) => s.category).filter(Boolean)));
  const catCards = categories.map((c) => ({ name: c, store: stores.find((s) => s.category === c) }));
  const featured = stores.filter((s) => s.featured);
  const carousel = featured.length ? featured : stores.slice(0, 8);

  // live phone feed — fetch real products for a few stores and rotate through them
  const [feeds, setFeeds] = useState([]);
  const [feedIdx, setFeedIdx] = useState(0);
  useEffect(() => {
    if (loading || !stores.length) return;
    let cancelled = false;
    const candidates = [...stores].sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0)).slice(0, 5);
    Promise.all(candidates.map((s) => api.get(`/public/stores/${s.handle}`).then((r) => r.data).catch(() => null)))
      .then((results) => {
        if (cancelled) return;
        const built = (results || [])
          .filter((d) => d && !d.locked && (d.products || []).length)
          .map((d) => ({
            store: d.store,
            images: (d.products || []).map((p) => imgUrl(p.image_url)).filter(Boolean).slice(0, 9),
            categories: (d.categories || []).slice(0, 5),
          }))
          .filter((f) => f.images.length >= 3);
        setFeeds(built);
      });
    return () => { cancelled = true; };
  }, [loading, stores.length]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (feeds.length <= 1) return;
    const t = setInterval(() => setFeedIdx((i) => (i + 1) % feeds.length), 4500);
    return () => clearInterval(t);
  }, [feeds.length]);
  const showLivePhone = !content.hero_image && feeds.length > 0;

  // split the eyebrow headline into two sticker halves
  const words = (content.hero_eyebrow || "The Instagram Concept Store").split(" ");
  const mid = Math.ceil(words.length / 2);
  const line1 = words.slice(0, mid).join(" ");
  const line2 = words.slice(mid).join(" ");

  return (
    <div className="relative min-h-screen text-white overflow-x-clip selection:bg-[#ccff00] selection:text-black v3root" style={{ background: BLUE }}>
      {/* collage textures */}
      <div className="pointer-events-none fixed inset-0 z-0 v3-halftone opacity-[0.5]" aria-hidden />
      <div className="pointer-events-none absolute top-0 right-0 w-40 h-40 v3-checker opacity-90 z-0" aria-hidden />
      <div className="pointer-events-none absolute top-[42vh] left-0 w-28 h-28 v3-checker opacity-80 z-0" aria-hidden />
      <ChromeStar className="pointer-events-none absolute top-24 right-10 w-14 h-14 z-[1] rotate-12 drop-shadow-lg" />
      <ChromeStar className="pointer-events-none absolute top-[38vh] right-4 w-9 h-9 z-[1] -rotate-6" />

      {/* Nav */}
      <div className="relative z-30 px-4 md:px-8 pt-5">
        <header className="max-w-7xl mx-auto flex items-center justify-between rounded-full bg-black pl-6 pr-2 h-16 border-2 border-black shadow-[6px_6px_0_rgba(0,0,0,0.35)]">
          <div style={anton} className="text-2xl tracking-wide" data-testid="directory-brand">
            STORE<span style={{ color: LIME }}>IG</span>
          </div>
          <nav className="hidden lg:flex items-center gap-8 text-sm font-bold text-white/80">
            <button onClick={() => scrollTo("stores")} className="hover:text-white transition-colors">Explore stores</button>
            <button onClick={() => scrollTo("categories")} className="hover:text-white transition-colors">Categories</button>
            <a href="#pricing" className="hover:text-white transition-colors">How it works</a>
            <button onClick={() => setSignupOpen(true)} className="hover:text-white transition-colors">For merchants</button>
          </nav>
          <div className="flex items-center gap-2 md:gap-4">
            <button onClick={() => scrollTo("stores")} aria-label="Search stores" className="hidden sm:grid place-items-center h-9 w-9 text-white/80 hover:text-white transition-colors"><Search size={18} /></button>
            <Link to="/login" data-testid="admin-cta" className="hidden sm:block text-sm font-bold text-white/85 hover:text-white transition-colors">Log in</Link>
            <button onClick={() => setSignupOpen(true)} data-testid="signup-cta"
              className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-extrabold text-black border-2 border-black active:scale-95 transition-transform hover:brightness-105"
              style={{ background: LIME }}>
              Become a merchant <ArrowRight size={15} />
            </button>
          </div>
        </header>
      </div>

      {/* Hero */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pt-10 md:pt-14 pb-8 grid lg:grid-cols-[1.05fr_0.95fr] gap-8 items-center">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <h1 style={anton} className="leading-[0.92]" data-testid="hero-title">
            <span className="inline-block bg-black text-white px-4 py-1.5 rounded-[6px] -rotate-1 text-5xl sm:text-6xl lg:text-7xl shadow-[5px_5px_0_rgba(0,0,0,0.3)] uppercase">{line1}</span>
            <br />
            <span className="inline-block mt-3 px-4 py-1.5 rounded-[6px] rotate-1 text-black text-5xl sm:text-6xl lg:text-7xl shadow-[5px_5px_0_rgba(0,0,0,0.3)] uppercase" style={{ background: LIME }}>{line2}</span>
          </h1>

          <div className="mt-7 inline-block px-4 py-2.5 -rotate-1 text-white font-extrabold text-xl md:text-2xl leading-tight shadow-[4px_4px_0_rgba(0,0,0,0.3)]" style={{ background: PINK }} data-testid="hero-eyebrow">
            {content.hero_title}
          </div>

          <div className="mt-5 inline-block max-w-md bg-white text-slate-800 px-5 py-4 rounded-[4px] rotate-[0.5deg] shadow-[4px_4px_0_rgba(0,0,0,0.25)] font-semibold leading-relaxed" data-testid="hero-subtitle">
            {content.hero_subtitle}
          </div>

          <div className="mt-8 flex flex-wrap gap-4">
            <button onClick={() => scrollTo("stores")} data-testid="hero-explore"
              className="group inline-flex items-center gap-2 rounded-full px-7 py-4 text-sm font-extrabold text-black border-2 border-black active:scale-95 transition-transform hover:brightness-105 shadow-[4px_4px_0_rgba(0,0,0,0.35)]"
              style={{ background: LIME }}>
              {content.cta_primary_label} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </button>
            <button onClick={() => setSignupOpen(true)}
              className="inline-flex items-center gap-2 rounded-full px-7 py-4 text-sm font-extrabold bg-black text-white border-2 border-black active:scale-95 transition-transform shadow-[4px_4px_0_rgba(0,0,0,0.35)]">
              {content.cta_secondary_label} <ArrowRight size={16} />
            </button>
          </div>

          {/* reviews window sticker */}
          {!loading && stores.length > 0 && (
            <div className="mt-9 inline-block bg-white text-slate-900 rounded-[6px] border-2 border-black shadow-[5px_5px_0_rgba(0,0,0,0.3)] -rotate-1 overflow-hidden" data-testid="hero-reviews-badge">
              <div className="flex items-center justify-between bg-slate-200 px-3 py-1 border-b-2 border-black">
                <span className="text-[11px] font-bold text-slate-700">Loved by shoppers &amp; creators</span>
                <span className="flex gap-1"><i className="h-2.5 w-2.5 rounded-full bg-slate-400 inline-block" /><i className="h-2.5 w-2.5 rounded-full bg-slate-400 inline-block" /><i className="h-2.5 w-2.5 rounded-full bg-slate-400 inline-block" /></span>
              </div>
              <div className="px-4 py-3 flex items-center gap-3">
                <div className="flex -space-x-2.5">
                  {stores.slice(0, 5).map((s) => (
                    <img key={s.id} src={imgUrl(s.logo_url)} alt="" className="h-8 w-8 rounded-full object-cover ring-2 ring-white bg-slate-100" />
                  ))}
                </div>
                <div>
                  <div className="flex gap-0.5">{[...Array(5)].map((_, i) => <Star key={i} size={13} className="fill-amber-400 stroke-amber-400" />)}</div>
                  <div className="text-xs font-bold mt-0.5">4.9/5 · <b>{stores.length}</b> live storefronts</div>
                </div>
              </div>
            </div>
          )}
        </motion.div>

        {/* Hero art — live phone feed (falls back to sticker image) */}
        <motion.div initial={{ opacity: 0, scale: 0.96, rotate: 2 }} animate={{ opacity: 1, scale: 1, rotate: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
          className="relative flex justify-center lg:justify-end">
          <div className="relative">
            <div className="absolute -inset-3 rounded-[2rem] -z-10 blur-2xl opacity-70" style={{ background: `radial-gradient(circle at 60% 40%, ${PINK}, transparent 65%)` }} />
            <ChromeStar className="pointer-events-none absolute -top-7 -left-5 w-11 h-11 -rotate-12 z-20 drop-shadow" />
            <Heart className="pointer-events-none absolute top-10 -right-3 w-7 h-7 z-20 fill-[#ff2d8e] stroke-[#ff2d8e] rotate-12" />
            {showLivePhone ? (
              <LivePhone feed={feeds[feedIdx % feeds.length]} />
            ) : (
              <div className="bg-white p-2.5 rounded-[1.6rem] border-[3px] border-black shadow-[10px_10px_0_rgba(0,0,0,0.35)] rotate-1">
                <img src={content.hero_image ? imgUrl(content.hero_image) : HERO_IMG} alt="Shop the feed on StoreIG"
                  className="w-full max-w-[560px] h-[360px] md:h-[460px] object-cover rounded-[1.2rem]" />
              </div>
            )}
          </div>
        </motion.div>
      </section>

      {/* Category cards row */}
      {catCards.length > 0 && (
        <section id="categories" className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pt-8">
          <div className="flex items-end justify-between mb-5">
            <h2 style={anton} className="text-3xl md:text-4xl uppercase tracking-wide">Shop by category</h2>
          </div>
          <div className="flex gap-4 overflow-x-auto pb-3 v3-scroll" data-testid="category-row">
            {catCards.map((c, i) => (
              <button key={c.name} onClick={() => scrollTo("stores")} data-testid={`category-card-${c.name}`}
                className="group shrink-0 w-40 bg-white rounded-2xl border-2 border-black shadow-[5px_5px_0_rgba(0,0,0,0.3)] overflow-hidden text-left active:scale-95 transition-transform hover:-translate-y-1">
                <div className="h-28 bg-slate-100 overflow-hidden">
                  {c.store ? <img src={imgUrl(c.store.banner_url || c.store.logo_url)} alt={c.name} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-300" /> : null}
                </div>
                <div className="px-3 py-3">
                  <div className="font-extrabold text-slate-900 text-sm truncate">{c.name}</div>
                  <div className="h-1.5 w-10 rounded-full mt-2" style={{ background: CAT_ACCENTS[i % CAT_ACCENTS.length] }} />
                </div>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Featured stores carousel */}
      <FeaturedStores heading={content.stores_heading} stores={carousel} loading={loading} onExplore={() => scrollTo("stores")} />

      {/* All stores grid */}
      <section id="stores" className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pt-20">
        <h2 style={anton} className="text-4xl md:text-5xl uppercase tracking-wide mb-8">All stores</h2>
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[...Array(6)].map((_, i) => <div key={i} className="h-64 rounded-2xl bg-white/10 animate-pulse" />)}
          </div>
        ) : stores.length === 0 ? (
          <p className="text-white/70 py-16" data-testid="stores-empty">No storefronts live yet.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="store-directory-grid">
            {stores.map((s) => <StoreCard key={s.id} s={s} />)}
          </div>
        )}
      </section>

      {/* Pricing */}
      {content.pricing?.length ? (
        <section id="pricing" className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pt-24" data-testid="pricing-section">
          <div className="text-center mb-12">
            <h2 style={anton} className="text-4xl md:text-6xl uppercase tracking-wide">{content.pricing_heading}</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6 items-stretch">
            {content.pricing.map((p, i) => (
              <div key={p.id || `price-${i}`} data-testid={`pricing-box-${i}`}
                className={`rounded-2xl p-8 flex flex-col border-2 border-black shadow-[6px_6px_0_rgba(0,0,0,0.35)] ${p.highlight ? "bg-[#ccff00] text-black" : "bg-white text-slate-900"}`}>
                <div className="flex items-center justify-between">
                  <span style={anton} className="text-2xl tracking-wide uppercase">{p.name}</span>
                  {p.highlight && <span className="text-[10px] font-extrabold uppercase tracking-widest bg-black text-[#ccff00] rounded-full px-2.5 py-1">Popular</span>}
                </div>
                <div className="mt-4 flex items-end gap-1">
                  <span style={anton} className="text-6xl tracking-tight">{p.price}</span>
                  <span className={`mb-2 text-sm ${p.highlight ? "text-black/60" : "text-slate-400"}`}>{p.period}</span>
                </div>
                <ul className="mt-6 space-y-3 flex-1">
                  {(p.features || []).map((f, fi) => (
                    <li key={fi} className="flex items-center gap-2.5 text-sm font-semibold">
                      <span className={`h-5 w-5 rounded-full flex items-center justify-center shrink-0 ${p.highlight ? "bg-black text-[#ccff00]" : "text-white"}`} style={p.highlight ? {} : { background: PINK }}><Check size={12} /></span> {f}
                    </li>
                  ))}
                </ul>
                <button onClick={() => handlePlan(p, i)} disabled={subscribing === i} data-testid={`pricing-cta-${i}`}
                  className={`mt-8 rounded-full py-3.5 text-sm font-extrabold border-2 border-black transition-transform active:scale-95 flex items-center justify-center gap-2 disabled:opacity-60 ${p.highlight ? "bg-black text-white" : "bg-[#ccff00] text-black"}`}>
                  {subscribing === i && <Loader2 size={15} className="animate-spin" />} {p.cta}
                </button>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {/* Reviews */}
      {content.reviews?.length ? <ReviewsV3 heading={content.reviews_heading} reviews={content.reviews} /> : null}

      {/* Footer */}
      <footer className="relative z-10 mt-24 bg-black text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16">
          <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-4">
            <div>
              <div style={anton} className="text-2xl tracking-wide">STORE<span style={{ color: LIME }}>IG</span></div>
              <p className="text-sm text-white/50 mt-3 max-w-xs">{content.footer_tagline}</p>
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.22em] text-white/40 mb-4">Explore</div>
              <ul className="space-y-2.5 text-sm text-white/60">
                <li><button onClick={() => scrollTo("stores")} className="hover:text-white transition-colors" data-testid="footer-stores">Stores</button></li>
                <li><button onClick={() => scrollTo("categories")} className="hover:text-white transition-colors">Categories</button></li>
                <li><button onClick={() => setSignupOpen(true)} className="hover:text-white transition-colors" data-testid="footer-signup">Become a merchant</button></li>
                <li><Link to="/login" className="hover:text-white transition-colors" data-testid="footer-login">Merchant login</Link></li>
              </ul>
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.22em] text-white/40 mb-4">Legal</div>
              <ul className="space-y-2.5 text-sm text-white/60">
                {LEGAL_LINKS.map((l) => (
                  <li key={l.slug}><Link to={`/legal/${l.slug}`} data-testid={`footer-legal-${l.slug}`} className="hover:text-white transition-colors">{l.label}</Link></li>
                ))}
                <li><button onClick={openCookieSettings} data-testid="footer-cookie-settings" className="hover:text-white transition-colors">Cookie settings</button></li>
              </ul>
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.22em] text-white/40 mb-4">More from us</div>
              <ul className="space-y-2.5 text-sm text-white/60">
                <li><a href="https://tiktokwebstore.com" target="_blank" rel="noreferrer" data-testid="footer-sister-tiktok" className="inline-flex items-center gap-1.5 hover:text-white transition-colors">TikTokWebstore.com <ArrowUpRight size={13} /></a></li>
                <li><a href="https://wawebstore.com" target="_blank" rel="noreferrer" data-testid="footer-sister-wa" className="inline-flex items-center gap-1.5 hover:text-white transition-colors">WAwebstore.com <ArrowUpRight size={13} /></a></li>
              </ul>
            </div>
          </div>
          <div className="mt-12 rounded-2xl border border-white/25 bg-white/[0.07] p-6" data-testid="footer-affiliation-note">
            <div className="flex items-center gap-2 text-white/85 mb-2">
              <Info size={17} strokeWidth={2} />
              <span className="text-xs font-bold uppercase tracking-[0.2em]">Independent platform</span>
            </div>
            <p className="text-sm text-white/70 leading-relaxed max-w-3xl font-medium" data-testid="footer-disclaimer">{content.disclaimer || DEFAULT_DISCLAIMER}</p>
          </div>
          <div className="mt-10 text-center">
            <div className="text-xs text-white/40">© {new Date().getFullYear()} StoreIG. All rights reserved.</div>
            <div className="mt-6 flex flex-col items-center gap-2" data-testid="footer-brand">
              <span className="text-[10px] uppercase tracking-[0.3em] text-white/30">A brand by</span>
              <a href={content.brand_url || "#"} target="_blank" rel="noreferrer" aria-label={content.brand_name || "Brand"}>
                {content.brand_logo_url
                  ? <img src={imgUrl(content.brand_logo_url)} alt={content.brand_name || "Brand"} data-testid="footer-brand-logo" className="h-14 w-auto opacity-90 hover:opacity-100 transition-opacity" />
                  : <span data-testid="footer-brand-name" style={anton} className="text-2xl tracking-wide">{content.brand_name}</span>}
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Signup dialog */}
      <Dialog open={signupOpen} onOpenChange={setSignupOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="signup-dialog">
          <DialogHeader><DialogTitle className="font-heading text-2xl">Join StoreIG</DialogTitle></DialogHeader>
          <DialogDescription className="text-sm text-slate-500 -mt-2">Tell us about your brand and we'll help you launch your store.</DialogDescription>
          <SignupForm onSuccess={() => setSignupOpen(false)} />
        </DialogContent>
      </Dialog>

      <style>{`
        .v3-halftone{background-image:radial-gradient(rgba(255,255,255,0.18) 1.5px, transparent 1.6px);background-size:14px 14px;}
        .v3-checker{background-image:conic-gradient(#000 90deg,#fff 0 180deg,#000 0 270deg,#fff 0);background-size:24px 24px;}
        .v3-scroll::-webkit-scrollbar{height:6px;}
        .v3-scroll::-webkit-scrollbar-thumb{background:rgba(0,0,0,0.35);border-radius:99px;}
      `}</style>
    </div>
  );
}

const LivePhone = ({ feed }) => {
  const s = feed.store;
  const accent = s.accent_color || "#1e3cff";
  return (
    <AnimatePresence mode="wait">
      <motion.div key={s.handle} initial={{ opacity: 0, y: 18, rotate: 3 }} animate={{ opacity: 1, y: 0, rotate: 1 }} exit={{ opacity: 0, y: -18 }}
        transition={{ duration: 0.5 }}
        className="relative w-[300px] md:w-[330px] rounded-[2.4rem] bg-black p-2 border-[3px] border-black shadow-[12px_12px_0_rgba(0,0,0,0.35)]"
        data-testid="hero-live-phone">
        <div className="rounded-[2rem] bg-white overflow-hidden">
          <div className="relative h-5 flex items-center justify-center"><span className="h-1.5 w-16 rounded-full bg-black/80" /></div>
          {/* header */}
          <div className="flex items-center justify-between px-4 py-2 border-b border-zinc-100">
            <ChevronLeft size={16} className="text-zinc-700" />
            <div className="flex items-center gap-1 text-sm font-extrabold text-zinc-900 truncate max-w-[180px]">@{s.handle} <BadgeCheck size={13} className="text-[#1e3cff] fill-[#cfe0ff] shrink-0" /></div>
            <MoreHorizontal size={16} className="text-zinc-700" />
          </div>
          {/* profile */}
          <div className="flex items-center gap-3 px-4 py-3">
            <div className="p-[2px] rounded-full shrink-0" style={{ background: `linear-gradient(135deg, ${accent}, #fff, ${accent})` }}>
              <img src={imgUrl(s.logo_url)} alt="" className="h-12 w-12 rounded-full object-cover bg-zinc-100 border-2 border-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-extrabold text-zinc-900 truncate">{s.name}</div>
              <div className="text-[11px] text-zinc-400 truncate">{s.tagline || `@${s.handle}`}</div>
            </div>
          </div>
          <div className="px-4 pb-3">
            <Link to={`/${s.handle}`} data-testid={`hero-phone-follow-${s.handle}`}
              className="block text-center rounded-lg py-1.5 text-xs font-extrabold text-white active:scale-95 transition-transform" style={{ background: accent }}>
              Follow
            </Link>
          </div>
          {/* story highlights */}
          {feed.categories.length ? (
            <div className="flex gap-3 px-4 pb-3 overflow-hidden">
              {feed.categories.map((c) => (
                <div key={c} className="flex flex-col items-center gap-1 shrink-0">
                  <div className="h-11 w-11 rounded-full p-[2px]" style={{ background: `linear-gradient(135deg, ${LIME}, ${PINK})` }}>
                    <div className="h-full w-full rounded-full bg-zinc-100 flex items-center justify-center text-[9px] font-bold text-zinc-600">{c.slice(0, 2).toUpperCase()}</div>
                  </div>
                  <span className="text-[8px] text-zinc-500 max-w-[44px] truncate">{c}</span>
                </div>
              ))}
            </div>
          ) : null}
          {/* feed grid */}
          <div className="grid grid-cols-3 gap-0.5 bg-zinc-100">
            {feed.images.slice(0, 9).map((img, i) => (
              <Link key={i} to={`/${s.handle}`} className="aspect-square bg-zinc-200 overflow-hidden group">
                <img src={img} alt="" className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-300" onError={(e) => { e.currentTarget.style.opacity = 0; }} />
              </Link>
            ))}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

const StoreCard = ({ s }) => (  <Link to={`/${s.handle}`} data-testid={`store-card-${s.handle}`}
    className="group block bg-white text-slate-900 rounded-2xl overflow-hidden border-2 border-black shadow-[5px_5px_0_rgba(0,0,0,0.3)] hover:-translate-y-1.5 transition-transform duration-300">
    <div className="h-24 relative" style={{ background: s.banner_url ? undefined : `linear-gradient(135deg, ${s.accent_color}, #111)` }}>
      {s.banner_url && <img src={imgUrl(s.banner_url)} alt="" className="w-full h-full object-cover" />}
      {s.featured && (
        <span className="absolute top-3 left-3 z-10 inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-widest rounded-full px-2.5 py-1 text-black border border-black" style={{ background: LIME }} data-testid={`featured-badge-${s.handle}`}>
          <Star size={10} className="fill-black" /> Featured
        </span>
      )}
    </div>
    <div className="relative z-10 px-6 pb-6 -mt-9">
      <div className="flex items-end justify-between">
        <div className="p-[2.5px] rounded-full border-2 border-black bg-white">
          <img src={imgUrl(s.logo_url)} alt={s.name} className="h-16 w-16 rounded-full object-cover bg-zinc-100" />
        </div>
        <ArrowUpRight className="text-zinc-300 group-hover:text-black transition-colors mb-2" />
      </div>
      <div className="flex items-center gap-1.5 mt-3">
        <div style={anton} className="text-xl tracking-wide">{s.name}</div>
        <BadgeCheck size={16} className="text-[#1e3cff] fill-[#cfe0ff]" />
      </div>
      <div className="text-sm text-zinc-400">@{s.handle}</div>
      <p className="text-zinc-500 mt-2 text-sm leading-relaxed line-clamp-2">{s.tagline}</p>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        {s.category ? <span className="inline-flex text-xs font-bold rounded-full px-3 py-1 bg-zinc-100 text-zinc-600">{s.category}</span> : null}
        <span className="inline-flex text-xs font-extrabold rounded-full px-3 py-1 bg-black text-white">{s.product_count} products</span>
      </div>
    </div>
  </Link>
);

const FeaturedStores = ({ heading, stores, loading, onExplore }) => {
  const ref = useRef(null);
  const scroll = (d) => ref.current?.scrollBy({ left: d * 360, behavior: "smooth" });
  if (loading || !stores.length) return null;
  return (
    <section className="relative z-10 bg-white text-slate-900 mt-16 py-14 border-y-[3px] border-black">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex items-center justify-between mb-8">
          <h2 style={anton} className="text-3xl md:text-5xl uppercase tracking-wide relative">
            {heading}
            <span className="block h-1.5 w-28 rounded-full mt-1" style={{ background: PINK }} />
          </h2>
          <div className="flex items-center gap-3">
            <button onClick={onExplore} data-testid="featured-view-all" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-extrabold hover:opacity-70 transition-opacity">View all stores <ArrowRight size={15} /></button>
            <button onClick={() => scroll(-1)} aria-label="Previous" data-testid="featured-prev" className="h-10 w-10 rounded-full border-2 border-black flex items-center justify-center hover:bg-black hover:text-white transition-colors active:scale-90"><ChevronLeft size={18} /></button>
            <button onClick={() => scroll(1)} aria-label="Next" data-testid="featured-next" className="h-10 w-10 rounded-full border-2 border-black flex items-center justify-center hover:bg-black hover:text-white transition-colors active:scale-90"><ChevronRight size={18} /></button>
          </div>
        </div>
        <div ref={ref} className="flex gap-5 overflow-x-auto pb-3 v3-scroll" data-testid="featured-carousel">
          {stores.map((s, i) => (
            <div key={s.id} className="shrink-0 w-[340px] rounded-2xl overflow-hidden border-2 border-black shadow-[6px_6px_0_rgba(0,0,0,0.25)] relative"
              style={{ background: `linear-gradient(135deg, ${s.accent_color}33, ${s.accent_color}14)` }} data-testid={`featured-card-${s.handle}`}>
              {s.banner_url ? <img src={imgUrl(s.banner_url)} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" /> : null}
              <div className="relative p-5 flex flex-col h-44 justify-between">
                <div className="flex items-center gap-3">
                  <img src={imgUrl(s.logo_url)} alt={s.name} className="h-14 w-14 rounded-full object-cover border-2 border-black bg-white" />
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span style={anton} className="text-xl tracking-wide truncate">{s.name}</span>
                      <BadgeCheck size={16} className="text-[#1e3cff] fill-white shrink-0" />
                    </div>
                    <div className="text-xs font-semibold text-slate-700">{s.category || `@${s.handle}`}</div>
                  </div>
                </div>
                <Link to={`/${s.handle}`} data-testid={`featured-follow-${s.handle}`}
                  className="self-start inline-flex items-center gap-1.5 rounded-full px-6 py-2.5 text-sm font-extrabold text-white bg-black border-2 border-black active:scale-95 transition-transform hover:brightness-110">
                  Follow
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const ReviewsV3 = ({ heading, reviews }) => {
  const [idx, setIdx] = useState(0);
  const go = (d) => setIdx((i) => (i + d + reviews.length) % reviews.length);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % reviews.length), 5000);
    return () => clearInterval(t);
  }, [reviews.length]);
  const r = reviews[idx];
  return (
    <section className="relative z-10 max-w-4xl mx-auto w-full px-6 pt-24" data-testid="reviews-section">
      <div className="text-center mb-10">
        <h2 style={anton} className="text-4xl md:text-6xl uppercase tracking-wide">{heading}</h2>
      </div>
      <div className="relative min-h-[220px] flex items-center justify-center">
        <AnimatePresence mode="wait">
          <motion.div key={idx} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.4 }}
            className="bg-white text-slate-900 rounded-3xl border-2 border-black shadow-[8px_8px_0_rgba(0,0,0,0.3)] px-8 md:px-12 py-10 text-center w-full" data-testid="review-slide">
            <div className="flex justify-center gap-1 mb-5">
              {[...Array(Math.max(0, Math.min(5, r.rating || 5)))].map((_, i) => (
                <Star key={i} size={18} className="fill-amber-400 stroke-amber-400" />
              ))}
            </div>
            <p className="font-extrabold text-2xl md:text-3xl leading-snug tracking-tight">"{r.text}"</p>
            <div className="mt-7 flex items-center justify-center gap-3">
              <div className="h-11 w-11 rounded-full flex items-center justify-center text-black font-extrabold border-2 border-black" style={{ background: LIME }}>{(r.name || "?").charAt(0)}</div>
              <div className="text-left">
                <div className="font-bold">{r.name}</div>
                <div className="text-slate-400 text-sm">{r.handle}</div>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
      <div className="flex items-center justify-center gap-4 mt-8">
        <button onClick={() => go(-1)} data-testid="review-prev" className="h-11 w-11 rounded-full border-2 border-white/60 hover:bg-white hover:text-black flex items-center justify-center transition-colors active:scale-95"><ChevronLeft size={18} /></button>
        <div className="flex gap-2">
          {reviews.map((_, i) => (
            <button key={i} onClick={() => setIdx(i)} data-testid={`review-dot-${i}`} className={`h-2 rounded-full transition-all ${i === idx ? "w-7" : "w-2 bg-white/40"}`} style={i === idx ? { background: LIME } : {}} />
          ))}
        </div>
        <button onClick={() => go(1)} data-testid="review-next" className="h-11 w-11 rounded-full border-2 border-white/60 hover:bg-white hover:text-black flex items-center justify-center transition-colors active:scale-95"><ChevronRight size={18} /></button>
      </div>
    </section>
  );
};
